import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Select, DatePicker, message } from 'antd';
import axios from 'axios';
import Sidebar from '../../components/Sidebar/Sidebar';
import ExcelJS from 'exceljs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faFileInvoice, 
  faFileExcel, 
  faEye, 
  faDownload, 
  faPen, 
  faCreditCard, 
  faTimesCircle,
  faCalendarAlt,
  faArrowRight,
  faExclamationCircle,
  faCircleExclamation,
  faHistory,
  faCheckCircle,
  faBan,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import { buildApiUrl } from '../../config/api.config';

const { Option } = Select;

const StatusBadge = ({ status, dueDate }) => {
  const isOverdue = (status === 'unpaid' || status === 'partially_paid') && 
                   dueDate && new Date(dueDate) < new Date();

  const getStatusCore = () => {
    switch(status) {
      case 'paid': return { color: 'var(--tf-success)', label: 'Settled', icon: faCheckCircle };
      case 'partially_paid': return { color: '#FF9800', label: 'Partial', icon: faHistory };
      case 'cancelled': return { color: 'var(--tf-text-muted)', label: 'Voided', icon: faBan };
      default: return isOverdue ? { color: 'var(--tf-error)', label: 'Overdue', icon: faExclamationCircle } : { color: '#F44336', label: 'Unpaid', icon: faExclamationCircle };
    }
  };

  const config = getStatusCore();

  return (
    <div className="tf-flex" style={{ 
      gap: '0.5rem', 
      padding: '0.4rem 0.8rem', 
      borderRadius: '20px', 
      background: `${config.color}15`, 
      border: `1px solid ${config.color}30`,
      color: config.color,
      width: 'fit-content'
    }}>
      <FontAwesomeIcon icon={config.icon} style={{ fontSize: '0.8rem' }} />
      <span style={{ fontSize: '0.75rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        {config.label}
      </span>
    </div>
  );
};

const PaymentModal = ({ open, onCancel, onSubmit, paymentData, setPaymentData, selectedInvoice, loading }) => {
  const calculateDueAmount = (invoice) => {
    if (!invoice) return 0;
    const totalPaid = invoice.payments?.reduce((sum, payment) => sum + payment.amount, 0) || 0;
    const amount = invoice.finalAmount || invoice.totalAmount || 0;
    return Math.max(0, amount - totalPaid);
  };

  const dueAmount = selectedInvoice ? calculateDueAmount(selectedInvoice) : 0;

  const getReferenceConfig = () => {
    switch (paymentData.paymentMethod) {
      case 'card': 
        return { 
          label: 'Auth Code / Last 4', 
          placeholder: 'Enter 4-6 digits', 
          required: true,
          validate: (val) => /^\d{4,6}$/.test(val || ''),
          error: 'Please enter exactly 4 to 6 numeric digits'
        };
      case 'upi': 
        return { 
          label: 'UTR Number (12 Digits)', 
          placeholder: 'Standard 12-digit UTR', 
          required: true,
          validate: (val) => /^\d{12}$/.test(val || ''),
          error: 'UPI UTR must be a precise 12-digit numeric code'
        };
      case 'bank_transfer': 
        return { 
          label: 'IMPS / NEFT Ref', 
          placeholder: 'Min. 8 alphanumeric chars', 
          required: true,
          validate: (val) => /^[a-zA-Z0-9]{8,22}$/.test(val || ''),
          error: 'Bank reference should be 8-22 alphanumeric characters'
        };
      default: 
        return { 
          label: 'Note (Optional)', 
          placeholder: 'Internal memo...', 
          required: false,
          validate: () => true
        };
    }
  };

  const refConfig = getReferenceConfig();
  const isRefFilled = paymentData.reference && paymentData.reference.trim().length > 0;
  const isRefValid = !refConfig.required || (isRefFilled && refConfig.validate(paymentData.reference));
  const isAmountValid = paymentData.amount && parseFloat(paymentData.amount) > 0 && parseFloat(paymentData.amount) <= (dueAmount + 0.01);

  return (
    <Modal
      open={open}
      onOk={onSubmit}
      onCancel={onCancel}
      footer={null}
      centered
      width={720}
      className="payment-modal"
    >
      <div style={{ background: 'var(--tf-bg-card)', borderRadius: '16px', overflow: 'hidden' }}>
        {/* Compact Header */}
        <div style={{ padding: '1.25rem 2rem', background: 'linear-gradient(90deg, rgba(255, 65, 53, 0.08) 0%, transparent 100%)', borderBottom: '1px solid var(--tf-border)', display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'rgba(255, 65, 53, 0.1)', color: 'var(--tf-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem' }}>
            <FontAwesomeIcon icon={faCreditCard} />
          </div>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '800', color: 'var(--tf-text-white)' }}>Process Settlement</h2>
            <p style={{ margin: 0, color: 'var(--tf-text-muted)', fontSize: '0.75rem' }}>Direct Entry for Invoice #{selectedInvoice?.invoiceNumber}</p>
          </div>
        </div>

        <div className="tf-grid" style={{ gridTemplateColumns: '260px 1fr', gap: '0', padding: 0 }}>
          {/* Left Panel: Financial Summary */}
          <div style={{ padding: '2rem', background: 'rgba(255,255,255,0.01)', borderRight: '1px solid var(--tf-border)', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <span style={{ fontSize: '0.7rem', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--tf-text-muted)', marginBottom: '0.5rem' }}>Outstanding Balance</span>
            <div style={{ fontSize: '2.2rem', fontWeight: '900', color: 'var(--tf-text-white)', lineHeight: '1' }}>
              ₹{dueAmount.toFixed(2)}
            </div>
            
            <div style={{ marginTop: '2.5rem', padding: '1rem', borderRadius: '10px', background: 'rgba(255, 152, 0, 0.05)', border: '1px solid rgba(255, 152, 0, 0.1)' }}>
               <div className="tf-flex" style={{ gap: '0.5rem', marginBottom: '0.4rem' }}>
                  <FontAwesomeIcon icon={faHistory} style={{ color: '#FF9800', fontSize: '0.8rem' }} />
                  <span style={{ fontSize: '0.75rem', fontWeight: '700', color: '#FF9800' }}>Payment Status</span>
               </div>
               <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--tf-text-muted)' }}>{dueAmount > 0 ? 'Awaiting settlement funds' : 'Fully compensated'}</p>
            </div>
          </div>

          {/* Right Panel: Data Entry */}
          <div style={{ padding: '2rem' }}>
            <div className="tf-grid" style={{ gap: '1.25rem' }}>
              <div className="tf-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <p className="tf-text-accent" style={{ fontSize: '0.7rem', fontWeight: '800', textTransform: 'uppercase', color: 'var(--tf-text-muted)', marginBottom: '0.5rem' }}>Amount</p>
                  <div style={{ position: 'relative' }}>
                    <span style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--tf-text-muted)', fontWeight: '700' }}>₹</span>
                    <input
                      className="tf-input"
                      type="number"
                      style={{ paddingLeft: '2.2rem', fontSize: '1rem', fontWeight: '700' }}
                      value={paymentData.amount}
                      onChange={(e) => setPaymentData({...paymentData, amount: e.target.value})}
                      placeholder="0.00"
                    />
                  </div>
                </div>
                <div>
                  <p className="tf-text-accent" style={{ fontSize: '0.7rem', fontWeight: '800', textTransform: 'uppercase', color: 'var(--tf-text-muted)', marginBottom: '0.5rem' }}>Method</p>
                  <select
                    className="tf-input"
                    value={paymentData.paymentMethod}
                    onChange={(e) => setPaymentData({...paymentData, paymentMethod: e.target.value, reference: ''})}
                  >
                    <option value="cash">Cash</option>
                    <option value="card">Card</option>
                    <option value="upi">UPI / QR</option>
                    <option value="bank_transfer">Bank</option>
                  </select>
                </div>
              </div>

              <div>
                <div className="tf-flex" style={{ justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <p className="tf-text-accent" style={{ fontSize: '0.7rem', fontWeight: '800', textTransform: 'uppercase', color: 'var(--tf-text-muted)', margin: 0 }}>{refConfig.label}</p>
                  {refConfig.required && <span style={{ fontSize: '0.65rem', color: 'var(--tf-error)', fontWeight: '800' }}>REQUIRED</span>}
                </div>
                <input
                  className="tf-input"
                  style={{ 
                    borderColor: (refConfig.required && isRefFilled && !refConfig.validate(paymentData.reference)) ? 'var(--tf-error)' : ''
                  }}
                  value={paymentData.reference}
                  onChange={(e) => setPaymentData({...paymentData, reference: e.target.value})}
                  placeholder={refConfig.placeholder}
                />
                {refConfig.required && (!isRefFilled || !refConfig.validate(paymentData.reference)) && (
                  <p style={{ color: isRefFilled ? 'var(--tf-error)' : 'rgba(255,255,255,0.2)', fontSize: '0.65rem', marginTop: '0.4rem', fontWeight: '500' }}>
                    {isRefFilled ? refConfig.error : `Please provide a valid ${paymentData.paymentMethod} reference`}
                  </p>
                )}
              </div>

              <div className="tf-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '1rem' }}>
                <button className="tf-btn" style={{ background: 'rgba(255,255,255,0.05)', color: 'white', fontSize: '0.9rem' }} onClick={onCancel}>Cancel</button>
                <button 
                  className="tf-btn tf-btn-primary" 
                  style={{ fontWeight: '800', fontSize: '0.9rem', opacity: (!isAmountValid || !isRefValid) ? 0.5 : 1 }}
                  onClick={onSubmit}
                  disabled={loading || !isAmountValid || !isRefValid}
                >
                  {loading ? 'Processing...' : 'Apply Settlement'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

const CancelModal = ({ open, onCancel, onSubmit, selectedInvoice, loading }) => {
  const calculatePaidAmount = (invoice) => {
    if (!invoice) return 0;
    return invoice.payments?.reduce((sum, payment) => sum + payment.amount, 0) || 0;
  };

  return (
    <Modal
      title={
        <div className="tf-flex" style={{ gap: '0.75rem', color: 'var(--tf-error)' }}>
          <FontAwesomeIcon icon={faTimesCircle} />
          <span>Void Invoice: #{selectedInvoice?.invoiceNumber}</span>
        </div>
      }
      open={open}
      onOk={onSubmit}
      onCancel={onCancel}
      okText="Confirm Void Request"
      cancelText="Keep Invoice"
      okButtonProps={{ danger: true }}
      confirmLoading={loading}
      centered
    >
      <div className="tf-grid" style={{ gap: '1.5rem', padding: '1rem 0' }}>
        <p style={{ color: 'var(--tf-text-muted)', lineHeight: '1.6' }}>You are about to permanently void this transaction record. This action will release any locked table reservations and revert the billing state.</p>
        
        <div className="tf-flex" style={{ justifyContent: 'space-between', padding: '1rem', background: 'rgba(255,56,96,0.05)', borderRadius: '8px', border: '1px solid rgba(255,56,96,0.1)' }}>
          <div>
            <span style={{ fontSize: '0.7rem', textTransform: 'uppercase', color: 'var(--tf-error)', fontWeight: '700' }}>Amount Recovered</span>
            <div style={{ fontSize: '1.2rem', fontWeight: '700' }}>₹{calculatePaidAmount(selectedInvoice).toFixed(2)}</div>
          </div>
          <StatusBadge status={selectedInvoice?.status} dueDate={selectedInvoice?.dueDate} />
        </div>

        {selectedInvoice?.status === 'paid' && (
          <div className="tf-flex" style={{ gap: '1rem', padding: '1rem', background: 'rgba(255,152,0,0.1)', borderRadius: '8px', border: '1px solid rgba(255,152,0,0.2)', color: '#FF9800' }}>
            <FontAwesomeIcon icon={faExclamationCircle} />
            <p style={{ margin: 0, fontSize: '0.85rem', fontWeight: '500' }}>Notice: This invoice is marked as Paid. Voiding will require manual refund coordination.</p>
          </div>
        )}
      </div>
    </Modal>
  );
};

const InvoiceListPage = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [cancelModalVisible, setCancelModalVisible] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [paymentData, setPaymentData] = useState({
    amount: '',
    paymentMethod: 'cash',
    reference: '',
  });
  const [exportLoading, setExportLoading] = useState(false);
  const [rangeModalVisible, setRangeModalVisible] = useState(false);
  const [dateRange, setDateRange] = useState([null, null]);
  const navigate = useNavigate();

  const calculateDueAmount = (invoice) => {
    if (!invoice) return 0;
    const totalPaid = invoice.payments?.reduce((sum, payment) => sum + payment.amount, 0) || 0;
    const amount = invoice.finalAmount || invoice.totalAmount || 0;
    return Math.max(0, amount - totalPaid);
  };

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const userDetailsStr = localStorage.getItem("userDetails");

      // Don't make API call if no token
      if (!token) {
        setInvoices([]);
        setLoading(false);
        return;
      }

      let user;
      try {
        user = JSON.parse(userDetailsStr);
      } catch (e) {
        setInvoices([]);
        setLoading(false);
        return;
      }

      const selectedRestId = localStorage.getItem("currentRestaurantId");
      let activeRestaurantId = user?.role === 'owner' ? selectedRestId : user?.restaurantId;

      // Handle populated restaurantId object
      if (activeRestaurantId && typeof activeRestaurantId === 'object') {
          activeRestaurantId = activeRestaurantId._id;
      }

      if (!activeRestaurantId) {
        setInvoices([]);
        setLoading(false);
        return;
      }

      const response = await axios.get(buildApiUrl(`/invoice/admin/all-invoice?restaurantId=${activeRestaurantId}`), {
        headers: { 'auth-token': token }
      });
      if (response.data && Array.isArray(response.data)) {
        setInvoices(response.data);
      } else {
        setInvoices([]);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        console.log('Token expired or invalid. Please login again.');
      } else {
        message.error('Failed to synchronize invoice data');
      }
    } finally {
      setLoading(false);
    }
  };

  const recordPayment = async () => {
    try {
      if (!selectedInvoice) return;
      if (!paymentData.amount || isNaN(paymentData.amount) || parseFloat(paymentData.amount) <= 0) {
        message.error('Invalid payment amount');
        return;
      }

      const dueAmount = calculateDueAmount(selectedInvoice);
      if (parseFloat(paymentData.amount) > dueAmount) {
        message.warning(`Amount exceeds outstanding balance (₹${dueAmount.toFixed(2)})`);
        return;
      }

      setLoading(true);
      const user = JSON.parse(localStorage.getItem("userDetails"));
      const selectedRestId = localStorage.getItem("currentRestaurantId");
      let activeRestaurantId = user.role === 'owner' ? selectedRestId : user.restaurantId;

      if (activeRestaurantId && typeof activeRestaurantId === 'object') {
          activeRestaurantId = activeRestaurantId._id;
      }

      const response = await axios.post(
        buildApiUrl(`/invoice/admin/${selectedInvoice._id}/record-payment`),
        {
          amount: parseFloat(paymentData.amount),
          paymentMethod: paymentData.paymentMethod,
          reference: paymentData.reference,
          customerName: selectedInvoice.customerName || selectedInvoice.userId?.name,
          restaurantId: activeRestaurantId
        },
        { headers: { 'auth-token': localStorage.getItem('token') } }
      );

      setInvoices(prev => prev.map(inv => inv._id === selectedInvoice._id ? {
        ...response.data.invoice,
        customerName: selectedInvoice.customerName || selectedInvoice.userId?.name || inv.customerName
      } : inv));
      
      message.success('Transaction documented successfully');
      setPaymentModalVisible(false);
    } catch (error) {
      message.error('Transaction failed to post');
    } finally {
      setLoading(false);
    }
  };

  const cancelInvoice = async () => {
    try {
      if (!selectedInvoice) return;
      setLoading(true);
      const user = JSON.parse(localStorage.getItem("userDetails"));
      const selectedRestId = localStorage.getItem("currentRestaurantId");
      let activeRestaurantId = user.role === 'owner' ? selectedRestId : user.restaurantId;

      if (activeRestaurantId && typeof activeRestaurantId === 'object') {
          activeRestaurantId = activeRestaurantId._id;
      }

      const response = await axios.patch(
        buildApiUrl(`/invoice/admin/${selectedInvoice._id}/cancel`),
        { 
          cancellationReason: 'Administrative Override',
          restaurantId: activeRestaurantId
        },
        { headers: { 'auth-token': localStorage.getItem('token') } }
      );
      setInvoices(prev => prev.map(inv => inv._id === selectedInvoice._id ? response.data.invoice : inv));
      message.success('Invoice records voided');
      setCancelModalVisible(false);
    } catch (error) {
      message.error('Failed to void invoice');
    } finally {
      setLoading(false);
    }
  };

  const deleteInvoice = async () => {
    try {
      if (!selectedInvoice) return;
      setLoading(true);
      const user = JSON.parse(localStorage.getItem("userDetails"));
      const selectedRestId = localStorage.getItem("currentRestaurantId");
      let activeRestaurantId = user.role === 'owner' ? selectedRestId : user.restaurantId;

      if (activeRestaurantId && typeof activeRestaurantId === 'object') {
          activeRestaurantId = activeRestaurantId._id;
      }

      await axios.delete(buildApiUrl(`/invoice/admin/${selectedInvoice._id}?restaurantId=${activeRestaurantId}`), {
        headers: { 'auth-token': localStorage.getItem('token') }
      });
      setInvoices(prev => prev.filter(inv => inv._id !== selectedInvoice._id));
      message.success('Invoice record permanently removed');
      setDeleteModalVisible(false);
    } catch (error) {
      message.error('Failed to delete invoice record');
    } finally {
      setLoading(false);
    }
  };

  const showRangeModal = () => setRangeModalVisible(true);
  const handleRangeOk = () => {
    setRangeModalVisible(false);
    exportToExcel(dateRange[0], dateRange[1]);
  };

  const getCustomerName = (invoice) => {
    return invoice.customerName || invoice.userId?.name || invoice.billingDetails?.name || 'Walk-in Customer';
  };

  const exportToExcel = async (startDate, endDate) => {
    setExportLoading(true);
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Invoices');
      const formatDate = (date) => new Date(date).toLocaleDateString('en-GB').replace(/\//g, '-');

      worksheet.addRow(['TASTYFLOW MASTER INVOICE REPORT']);
      worksheet.mergeCells('A1:G1');
      worksheet.addRow([]);
      worksheet.addRow([startDate && endDate ? `Period: ${formatDate(startDate)} to ${formatDate(endDate)}` : 'Period: Full Lifetime History']);
      worksheet.addRow([`Report Generated: ${new Date().toLocaleString()}`]);
      worksheet.addRow([]);
      worksheet.addRow(['Invoice #', 'Date', 'Customer', 'Net Amount', 'Paid', 'Balance', 'Status']);

      invoices.forEach(inv => {
        const paid = inv.payments?.reduce((sum, p) => sum + (p.amount || 0), 0) || 0;
        worksheet.addRow([
          inv.invoiceNumber || 'N/A',
          formatDate(inv.invoiceDate),
          getCustomerName(inv),
          inv.finalAmount || inv.totalAmount || 0,
          paid,
          Math.max(0, (inv.finalAmount || inv.totalAmount || 0) - paid),
          inv.status || 'Pending'
        ]);
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `TF_Invoice_Report_${formatDate(new Date())}.xlsx`;
      a.click();
      message.success('Master report exported');
    } catch (error) {
      message.error('Export sequence interrupted');
    } finally {
      setExportLoading(false);
    }
  };

  const downloadInvoicePDF = async (invoiceId) => {
    try {
      message.loading('Acquiring secure PDF source...', 1);
      const user = JSON.parse(localStorage.getItem("userDetails"));
      const selectedRestId = localStorage.getItem("currentRestaurantId");
      let activeRestaurantId = user.role === 'owner' ? selectedRestId : user.restaurantId;

      // Handle populated restaurantId object
      if (activeRestaurantId && typeof activeRestaurantId === 'object') {
          activeRestaurantId = activeRestaurantId._id;
      }

      const response = await axios.get(buildApiUrl(`/invoice/admin/${invoiceId}/download-pdf?restaurantId=${activeRestaurantId}&t=${Date.now()}`), { 
        responseType: 'blob',
        headers: { 'auth-token': localStorage.getItem('token') }
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Invoice_${invoiceId.slice(-6)}.pdf`;
      a.click();
    } catch (error) {
      message.error('PDF generation timed out');
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, []);

  return (
    <div className="admin-layout">
      <Sidebar />
      <main>
        <div className="tf-card" style={{ marginBottom: '2rem' }}>
          <header className="tf-flex" style={{ justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1.5rem', marginBottom: '3rem' }}>
            <div>
              <h1 className="tf-heading-md tf-flex" style={{ gap: '0.75rem', marginBottom: '0.5rem' }}>
                <FontAwesomeIcon icon={faFileInvoice} style={{ color: 'var(--tf-primary)' }} />
                Financial Registry
              </h1>
              <p style={{ color: 'var(--tf-text-muted)', fontSize: '0.95rem' }}>Centralized hub for monitoring customer billings and payment settlements</p>
            </div>
            
            <button 
              className="tf-btn tf-btn-primary" 
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.8rem 1.5rem' }}
              onClick={showRangeModal}
              disabled={exportLoading}
            >
              <FontAwesomeIcon icon={faFileExcel} />
              {exportLoading ? 'Compiling Registry...' : 'Master Export'}
            </button>
          </header>

          {loading ? (
            <div className="tf-flex-center" style={{ padding: '6rem 2rem', flexDirection: 'column', gap: '1.5rem' }}>
              <div className="tf-spinner"></div>
              <p style={{ color: 'var(--tf-text-muted)', fontWeight: '500' }}>Synchronizing registry records...</p>
            </div>
          ) : (
            <div className="tf-list-container">
              <div className="tf-list-header" style={{ gridTemplateColumns: '1.2fr 1.5fr 1.2fr 1fr 1fr 140px', gap: '1.5rem' }}>
                <span>ISSUE DATE</span>
                <span>CUSTOMER</span>
                <span>STATUS LOG</span>
                <span style={{ textAlign: 'right' }}>BASE AMOUNT</span>
                <span style={{ textAlign: 'right' }}>DUE</span>
                <span style={{ textAlign: 'right' }}>ACTION</span>
              </div>
              
              <div className="tf-grid" style={{ gap: '0' }}>
                {invoices.length > 0 ? (
                  invoices.map((invoice) => (
                    <div key={invoice._id} className="tf-list-item" style={{ gridTemplateColumns: '1.2fr 1.5fr 1.2fr 1fr 1fr 140px', gap: '1.5rem' }}>
                      <div className="tf-flex" style={{ gap: '0.75rem', color: 'var(--tf-text-muted)', fontSize: '0.9rem' }}>
                        <FontAwesomeIcon icon={faCalendarAlt} style={{ opacity: 0.5 }} />
                        {new Date(invoice.invoiceDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </div>
                      
                      <div style={{ fontWeight: '600', color: 'var(--tf-text-white)' }}>
                        {getCustomerName(invoice)}
                      </div>

                      <div>
                        <StatusBadge status={invoice.status} dueDate={invoice.dueDate} />
                      </div>

                      <div style={{ textAlign: 'right', fontWeight: '600' }}>
                        ₹{(invoice.finalAmount || invoice.totalAmount || 0).toFixed(2)}
                      </div>

                      <div style={{ textAlign: 'right' }}>
                        <span style={{ 
                          color: calculateDueAmount(invoice) > 0 ? 'var(--tf-primary)' : 'var(--tf-success)',
                          fontWeight: '700'
                        }}>
                          ₹{calculateDueAmount(invoice).toFixed(2)}
                        </span>
                      </div>

                      <div style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end', gap: '0.4rem' }}>
                        <button 
                          className="tf-btn" 
                          style={{ width: '32px', height: '32px', padding: 0, background: 'rgba(255,255,255,0.03)', color: 'var(--tf-text-muted)', borderRadius: '6px' }}
                          title="View Insights"
                          onClick={() => navigate(`/admin/invoices/${invoice._id}`)}
                        >
                          <FontAwesomeIcon icon={faEye} size="xs" />
                        </button>
                        <button 
                          className="tf-btn" 
                          style={{ width: '32px', height: '32px', padding: 0, background: 'rgba(255,255,255,0.03)', color: 'var(--tf-text-muted)', borderRadius: '6px' }}
                          title="Generate PDF"
                          onClick={() => downloadInvoicePDF(invoice._id)}
                        >
                          <FontAwesomeIcon icon={faDownload} size="xs" />
                        </button>
                        <button 
                          className="tf-btn" 
                          style={{ width: '32px', height: '32px', padding: 0, background: 'rgba(255,255,255,0.03)', color: 'var(--tf-text-muted)', borderRadius: '6px' }}
                          title="Modify Entry"
                          onClick={() => navigate(`/admin/invoices/edit/${invoice._id}`)}
                          disabled={invoice.status === 'cancelled'}
                        >
                          <FontAwesomeIcon icon={faPen} size="xs" />
                        </button>
                        <button 
                          className="tf-btn" 
                          style={{ width: '32px', height: '32px', padding: 0, background: 'rgba(255, 65, 53, 0.1)', color: 'var(--tf-primary)', borderRadius: '6px' }}
                          title="Apply Fund"
                          onClick={() => { setSelectedInvoice(invoice); setPaymentData({ amount: calculateDueAmount(invoice).toFixed(2), paymentMethod: 'cash', reference: '' }); setPaymentModalVisible(true); }}
                          disabled={invoice.status === 'paid' || invoice.status === 'cancelled'}
                        >
                          <FontAwesomeIcon icon={faCreditCard} size="xs" />
                        </button>
                        <button 
                          className="tf-btn" 
                          style={{ width: '32px', height: '32px', padding: 0, background: 'rgba(255,255,255,0.03)', color: 'var(--tf-error)', borderRadius: '6px' }}
                          title="Void Record"
                          onClick={() => { setSelectedInvoice(invoice); setCancelModalVisible(true); }}
                          disabled={invoice.status === 'cancelled'}
                        >
                          <FontAwesomeIcon icon={faTimesCircle} size="xs" />
                        </button>
                        <button 
                          className="tf-btn" 
                          style={{ width: '32px', height: '32px', padding: 0, background: 'rgba(255,255,255,0.03)', color: 'var(--tf-error)', borderRadius: '6px' }}
                          title="Delete Permanently"
                          onClick={() => { setSelectedInvoice(invoice); setDeleteModalVisible(true); }}
                        >
                          <FontAwesomeIcon icon={faTrash} size="xs" />
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="tf-empty-state">
                    <FontAwesomeIcon icon={faFileInvoice} size="3x" style={{ marginBottom: '1.5rem', opacity: 0.1 }} />
                    <p style={{ fontSize: '1.1rem', fontWeight: '500' }}>Financial registry is currently empty</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Auxiliary Overlays */}
      <Modal
        title={<div className="tf-flex" style={{ gap: '0.75rem' }}><FontAwesomeIcon icon={faCalendarAlt} /> Export Parameter Definition</div>}
        open={rangeModalVisible}
        onOk={handleRangeOk}
        onCancel={() => { setRangeModalVisible(false); setDateRange([null, null]); }}
        okText="Initialize Compilation"
        cancelText="Discard"
        centered
      >
        <div className="tf-grid" style={{ gap: '1.5rem', padding: '1rem 0' }}>
          <p style={{ color: 'var(--tf-text-muted)', fontSize: '0.9rem' }}>Define the date boundaries for the comprehensive financial export report.</p>
          <div className="tf-flex" style={{ gap: '1rem' }}>
            <DatePicker 
              placeholder="Start Boundary" 
              className="tf-input" 
              style={{ flex: 1 }} 
              onChange={(date) => setDateRange([date, dateRange[1]])} 
            />
            <FontAwesomeIcon icon={faArrowRight} style={{ color: 'var(--tf-border)' }} />
            <DatePicker 
              placeholder="End Boundary" 
              className="tf-input" 
              style={{ flex: 1 }} 
              onChange={(date) => setDateRange([dateRange[0], date])} 
            />
          </div>
          <button 
            className="tf-btn" 
            style={{ background: 'transparent', color: 'var(--tf-primary)', fontSize: '0.85rem', fontWeight: '600' }}
            onClick={() => { setDateRange([null, null]); exportToExcel(null, null); setRangeModalVisible(false); }}
          >
            Override Filter - Export All Unified Data
          </button>
        </div>
      </Modal>

      <PaymentModal
        open={paymentModalVisible}
        onCancel={() => setPaymentModalVisible(false)}
        onSubmit={recordPayment}
        paymentData={paymentData}
        setPaymentData={setPaymentData}
        selectedInvoice={selectedInvoice}
        loading={loading}
      />

      <CancelModal
        open={cancelModalVisible}
        onCancel={() => setCancelModalVisible(false)}
        onSubmit={cancelInvoice}
        selectedInvoice={selectedInvoice}
        loading={loading}
      />

      <Modal
        open={deleteModalVisible}
        onOk={deleteInvoice}
        onCancel={() => setDeleteModalVisible(false)}
        footer={null}
        centered
        width={500}
        className="destructive-modal"
      >
        <div style={{ background: 'var(--tf-bg-card)' }}>
          {/* Destructive Header */}
          <div style={{ padding: '2rem', background: 'linear-gradient(180deg, rgba(255, 65, 53, 0.1) 0%, transparent 100%)', textAlign: 'center', borderBottom: '1px solid var(--tf-border)' }}>
            <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: 'rgba(255, 65, 53, 0.1)', color: 'var(--tf-error)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.8rem', margin: '0 auto 1.5rem' }}>
              <FontAwesomeIcon icon={faTrash} />
            </div>
            <h2 style={{ margin: 0, fontSize: '1.4rem', fontWeight: '800', color: 'var(--tf-text-white)' }}>Confirm Permanent Deletion</h2>
            <p style={{ margin: '0.5rem 0 0', color: 'var(--tf-text-muted)', fontSize: '0.9rem' }}>Administrative override for Invoice #{selectedInvoice?.invoiceNumber}</p>
          </div>

          <div style={{ padding: '2rem' }}>
            {/* Status-Aware Impact Card */}
            <div style={{ padding: '1.5rem', borderRadius: '12px', background: 'rgba(255,255,255,0.02)', border: '1px solid var(--tf-border)', marginBottom: '2rem' }}>
              <div className="tf-flex" style={{ gap: '0.5rem', marginBottom: '1rem' }}>
                 <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'var(--tf-error)' }}></div>
                 <span style={{ fontSize: '0.7rem', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--tf-text-muted)' }}>Financial Impact Report</span>
              </div>

              {selectedInvoice?.status === 'paid' && (
                <div className="tf-grid" style={{ gap: '1rem' }}>
                  <div style={{ color: 'var(--tf-error)', fontWeight: '700', fontSize: '1rem', lineHeight: '1.4' }}>
                    Critical: Permanent Ledger Breach
                  </div>
                  <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--tf-text-muted)', lineHeight: '1.6' }}>
                    This invoice is fully settled. Deleting it will orphan the received funds in your records, creating a non-recoverable gap in your financial audit trail.
                  </p>
                </div>
              )}
              
              {selectedInvoice?.status === 'partially_paid' && (
                <div className="tf-grid" style={{ gap: '1rem' }}>
                  <div style={{ color: '#FF9800', fontWeight: '700', fontSize: '1rem', lineHeight: '1.4' }}>
                    Warning: Partial Payment Loss
                  </div>
                  <div className="tf-flex" style={{ justifyContent: 'space-between', padding: '0.75rem', background: 'rgba(255, 152, 0, 0.05)', borderRadius: '8px', border: '1px solid rgba(255, 152, 0, 0.1)' }}>
                    <span style={{ fontSize: '0.85rem' }}>Funds to be Orphaned:</span>
                    <span style={{ fontWeight: '800', color: '#FF9800' }}>₹{(selectedInvoice.payments?.reduce((s, p) => s + p.amount, 0) || 0).toFixed(2)}</span>
                  </div>
                </div>
              )}
              
              {(selectedInvoice?.status === 'unpaid' || selectedInvoice?.status === 'overdue') && (
                <div className="tf-grid" style={{ gap: '1rem' }}>
                  <div style={{ color: 'var(--tf-text-white)', fontWeight: '700', fontSize: '1rem', lineHeight: '1.4' }}>
                    Standard Deletion Procedure
                  </div>
                  <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--tf-text-muted)', lineHeight: '1.6' }}>
                    This record is unpaid. Removal will clear the customer's outstanding balance of <strong>₹{(selectedInvoice?.finalAmount || selectedInvoice?.totalAmount || 0).toFixed(2)}</strong> from active duty.
                  </p>
                </div>
              )}

              {selectedInvoice?.status === 'cancelled' && (
                <div style={{ color: 'var(--tf-text-muted)', fontSize: '0.9rem', textAlign: 'center', fontStyle: 'italic' }}>
                  Voided record cleanup: Database optimization in progress.
                </div>
              )}
            </div>

            {/* Safety Checkbox could go here, but keeping it simple as requested */}
            <p style={{ color: 'var(--tf-error)', fontSize: '0.8rem', textAlign: 'center', marginBottom: '2rem', fontWeight: '600' }}>
              <FontAwesomeIcon icon={faCircleExclamation} /> This action cannot be reversed.
            </p>

            {/* Action Buttons */}
            <div className="tf-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <button 
                className="tf-btn" 
                style={{ padding: '1rem', background: 'rgba(255,255,255,0.05)', color: 'white', fontWeight: '600' }}
                onClick={() => setDeleteModalVisible(false)}
              >
                Retain Record
              </button>
              <button 
                className="tf-btn" 
                style={{ padding: '1rem', background: 'var(--tf-error)', color: 'white', fontWeight: '800' }}
                onClick={deleteInvoice}
                disabled={loading}
              >
                {loading ? 'Purging...' : 'Confirm Purge'}
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default InvoiceListPage;